import * as React from 'react';
import { useTranslation } from 'react-i18next';
import {
  PrimaryButton,
  Table,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@bb-ui/react-library/dist/components';
import { PageTemplate } from 'components/PageTemplate';
import { ErrorMessage } from 'components/ErrorMessage';
import { LoadingIndicator } from 'components/LoadingIndicator';
import { apiUrl } from 'utils/apiUrl';
import useRestApi from 'hooks/useRestApi';
import { IJobSummary } from '@lct-jobs/core';
import { JobRow } from 'components/JobList/JobRow';
import { useHistory } from 'react-router';

export const GlobalJobs: React.FC = () => {
  const { t } = useTranslation();
  const [searchQuery, setSearchQuery] = React.useState<string>('');
  const history = useHistory();
  const { data, loading, error } = useRestApi(apiUrl('lct', `tenants/global/jobManager/jobs`));
  const globalJobsList = React.useMemo<IJobSummary[]>(() => data?.jobs ?? [], [data]);

  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchQuery(event.target.value);
  };

  let content: React.ReactElement;

  if (error) {
    content = (
      <ErrorMessage
        data-testid="global-jobs-list-error"
        title={t('jobService.loadError')}
        message={error.message}
        variant="block"
      />
    );
  } else if (loading) {
    content = <LoadingIndicator data-testid="jobs-list-init" />;
  } else if (globalJobsList.length === 0) {
    content = <Typography data-testid="global-jobs-no-data">{t('jobService.noData')}</Typography>;
  } else {
    content = (
      <>
        <div
          style={{
            display: 'flex',
            alignItems: 'flex-end',
            justifyContent: 'space-between',
            marginBottom: '1rem',
          }}
        >
          <TextField
            floatingLabel={true}
            label={t('jobService.searchJobs')}
            value={searchQuery}
            onChange={handleSearchChange}
            margin="normal"
            style={{ marginRight: '1rem', marginBottom: 0 }}
          />
          <PrimaryButton
            data-testid="header-add-button-fleet-service"
            onClick={() => history.push(`/jobs/executions`)}
          >
            {t('jobService.viewExecutions')}
          </PrimaryButton>
        </div>
        <div>
          <Table>
            <TableHead>
              <TableRow key="$HEADER">
                <TableCell>{t('jobService.name')}</TableCell>
                <TableCell>{t('jobService.domain')}</TableCell>
                <TableCell>{t('jobService.description')}</TableCell>
                <TableCell>{t('jobService.submitJob')}</TableCell>
              </TableRow>
            </TableHead>
            <tbody>
              {globalJobsList.map((job) => (
                <JobRow key={job.jobId} jobSummary={job} tenantId="global" />
              ))}
            </tbody>
          </Table>
        </div>
      </>
    );
  }

  return (
    <PageTemplate title={t('jobService.pageTitle')} data-testid="global-jobs-list-page">
      {content}
    </PageTemplate>
  );
};

export default GlobalJobs;
